import {
  Component,
  inject,
  OnInit,
  CUSTOM_ELEMENTS_SCHEMA,
  signal,
  ViewChild,
} from '@angular/core';

import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';

import {
  AttendanceTrackingStore,
  GroupedExpert,
  SignInStatusFilter,
} from './store/attendance-tracking.store';
import { AttendanceStatsComponent } from './components/attendance-stats.component';
import { AlphabetSelectorComponent } from './components/alphabet-selector.component';
import { ExpertListComponent } from './components/expert-list.component';
import { SignInStatusFilterComponent } from './components/status-in-status-filter.component';
import {
  FeatureHeaderComponent,
  RefreshButtonComponent,
  ConfirmationDialogComponent,
  ErrorAlertComponent,
  ConfirmationType,
  ConfirmationDialogPayload,
  NoRecordsComponent,
} from '@sandbox-platform/shared-ui';

@Component({
  standalone: true,
  imports: [
    FormsModule,
    AttendanceStatsComponent,
    AlphabetSelectorComponent,
    ExpertListComponent,
    SignInStatusFilterComponent,
    FeatureHeaderComponent,
    RefreshButtonComponent,
    ConfirmationDialogComponent,
    ErrorAlertComponent,
    NoRecordsComponent
],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  selector: 'lib-attendance-tracking',
  templateUrl: './attendance-tracking.component.html',
})
export class AttendanceTrackingComponent implements OnInit {
  store = inject(AttendanceTrackingStore);
  router = inject(Router);

  // ViewChild for confirmation dialog
  @ViewChild('confirmationDialog')
  confirmationDialog!: ConfirmationDialogComponent;

  // Statistics visibility state
  isStatsVisible = signal(false);

  ngOnInit() {
    this.store.loadExperts();
  }

  onBackClick() {
    this.router.navigate(['/dashboard']);
  }

  onRefresh() {
    this.store.refresh();
  }

  onClearError() {
    this.store.clearError();
  }

  onToggleStats() {
    this.isStatsVisible.update((visible) => !visible);
  }

  onSearchTermChange(searchTerm: string) {
    this.store.updateSearchTerm(searchTerm);
  }

  onSignInStatusFilterChange(status: SignInStatusFilter) {
    this.store.updateSignInStatusFilter(status);
  }

  onResetFilters() {
    this.store.updateSearchTerm('');
    this.store.updateSignInStatusFilter('all');
    this.store.selectLetter(null);
  }

  onLetterSelect(letter: string | null) {
    this.store.selectLetter(letter);
  }

  onExpertSelect(expert: GroupedExpert) {
    this.store.selectExpert(expert);
  }

  onSignIn(expert: GroupedExpert) {
    this.store.selectExpert(expert);
    this.store.setPendingAction('signin');

    const payload: ConfirmationDialogPayload = {
      type: this.getConfirmationType(),
      title: this.getConfirmationTitle(),
      message: this.getConfirmationMessage(),
    };

    this.confirmationDialog.open(payload);
  }

  onSignOut(expert: GroupedExpert) {
    this.store.selectExpert(expert);
    this.store.setPendingAction('signout');

    const payload: ConfirmationDialogPayload = {
      type: this.getConfirmationType(),
      title: this.getConfirmationTitle(),
      message: this.getConfirmationMessage(),
    };

    this.confirmationDialog.open(payload);
  }

  confirmAction() {
    const expert = this.store.selectedExpert();
    const action = this.store.pendingAction();

    if (!expert || !action) return;

    if (action === 'signin') {
      this.store.recordSignIn({ expertId: expert.id });
    } else if (action === 'signout') {
      this.store.recordSignOut({ expertId: expert.id });
    }

    this.store.clearPendingAction();
  }

  cancelAction() {
    this.store.clearPendingAction();
  }

  getConfirmationTitle(): string {
    const action = this.store.pendingAction();
    return action === 'signin' ? 'Confirm Sign In' : 'Confirm Sign Out';
  }

  getConfirmationMessage(): string {
    const expert = this.store.selectedExpert();
    const action = this.store.pendingAction();

    if (!expert) return '';

    if (action === 'signin') {
      return `Sign in ${expert.full_name}`;
    } else {
      return `Sign out ${expert.full_name}`;
    }
  }

  getConfirmationAction(): string {
    const action = this.store.pendingAction();
    return action === 'signin' ? 'Sign In' : 'Sign Out';
  }

  getConfirmationType(): ConfirmationType {
    const action = this.store.pendingAction();
    return action === 'signin' ? 'low' : 'medium';
  }
}
